import React, { useMemo } from "react";

import moment from "moment";

import { getIsSubmissionPeriodOpen, useGetDataSubmissionDates, useGetFarmReportingYear, useGetFarms, useNavigate, useTranslation } from "utils";

import { ActionBanner } from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

enum BANNER_STATUS {
  NOT_STARTED = "notStarted",
  IN_PROGRESS = "inProgress",
  SUBMISSION_OPEN = "submissionOpen",
  SUBMISSION_OVERDUE = "submissionOverdue",
  SUBMISSION_REOPENED = "submissionReopened",
  SUBMISSION_REOPENED_OVERDUE = "submissionReopenedOverdue",
  SUBMISSION_COMPLETED = "submissionCompleted",
}

export const HomeSubmissionBanner = () => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { currentFarm } = useGetFarms();
  const { data: dataSubmissionDates } = useGetDataSubmissionDates({ farmId: currentFarm?.id });
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isDataSubmitted, isDataSubmissionReopened, submissionStatusValue } = useSubmissionStatus(activeFarmReportingYear);

  const today = moment();
  const isSubmissionPeriodOpen = useMemo(
    () => getIsSubmissionPeriodOpen(dataSubmissionDates?.submissionStartDate, dataSubmissionDates?.submissionEndDate),
    [dataSubmissionDates?.submissionStartDate, dataSubmissionDates?.submissionEndDate]
  );

  const isSubmissionOverduePeriod = useMemo(
    () => isSubmissionPeriodOpen && today.isSameOrAfter(moment(dataSubmissionDates?.submissionDueDate), "day"),
    [isSubmissionPeriodOpen, today, dataSubmissionDates?.submissionDueDate]
  );

  const bannerType = useMemo(() => {
    if (isDataSubmitted && !isDataSubmissionReopened) {
      return "info";
    }
    if (isSubmissionOverduePeriod) {
      return "error";
    }
    if (isSubmissionPeriodOpen) {
      return "warning";
    }
    return "info";
  }, [isDataSubmitted, isDataSubmissionReopened, isSubmissionOverduePeriod, isSubmissionPeriodOpen]);

  const bannerStatus = useMemo(() => {
    if (!isSubmissionPeriodOpen) {
      if (submissionStatusValue?.hasStartedDataFilling) {
        return BANNER_STATUS.IN_PROGRESS;
      }
      return BANNER_STATUS.NOT_STARTED;
    }

    if (isDataSubmitted && !isDataSubmissionReopened) {
      return BANNER_STATUS.SUBMISSION_COMPLETED;
    }

    if (isSubmissionOverduePeriod) {
      if (isDataSubmissionReopened) {
        return BANNER_STATUS.SUBMISSION_REOPENED_OVERDUE;
      }
      return BANNER_STATUS.SUBMISSION_OVERDUE;
    }

    if (isDataSubmissionReopened) {
      return BANNER_STATUS.SUBMISSION_REOPENED;
    }

    return BANNER_STATUS.SUBMISSION_OPEN;
  }, [submissionStatusValue?.hasStartedDataFilling, isDataSubmitted, isDataSubmissionReopened, isSubmissionOverduePeriod, isSubmissionPeriodOpen]);

  const ctaKey = bannerStatus === BANNER_STATUS.SUBMISSION_COMPLETED ? "viewDataCta" : "cta";

  return (
    <ActionBanner
      onClick={() => navigate("/all-fields")}
      title={t(`dataSubmissionBanner.status.${bannerStatus}.title`, { year: activeFarmReportingYear })}
      description={t(`dataSubmissionBanner.status.${bannerStatus}.description`, { year: activeFarmReportingYear })}
      buttonLabel={t(`dataSubmissionBanner.status.${bannerStatus}.${ctaKey}`, { year: activeFarmReportingYear })}
      type={bannerType}
      dueDate={moment(dataSubmissionDates?.submissionDueDate)}
    />
  );
};
